import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadSimple, FilmSlate, Sparkle, FileVideo, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", len: 60 },
  { id: "instagram", label: "Instagram", len: 45 },
  { id: "tiktok", label: "TikTok", len: 30 },
  { id: "youtube", label: "YouTube", len: 60 },
];

export default function VideoStudio() {
  const [file, setFile] = useState(null);
  const [platform, setPlatform] = useState("instagram");
  const [targetLen, setTargetLen] = useState(45);
  const [prompt, setPrompt] = useState("");
  const [genMode, setGenMode] = useState(false);
  const [motion, setMotion] = useState(false);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/video/runs").then(({ data }) => setRecent(data.slice(0, 5))).catch(() => {}); }, []);

  const pick = (f) => {
    if (!f) return;
    if (!/\.(mp4|mov|webm|mkv|m4v)$/i.test(f.name)) { toast.error("Use mp4 / mov video."); return; }
    setFile(f);
  };

  const generate = async () => {
    if (genMode ? !prompt.trim() : !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("platform", platform);
      fd.append("target_len", String(targetLen));
      fd.append("prompt", prompt);
      fd.append("mode", genMode ? "generate" : "edit");
      if (genMode) fd.append("motion", motion ? "true" : "false");
      const { data } = await api.post("/video/runs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/video/runs/${data.id}/start`);
      nav(`/video/runs/${data.id}`);
    } catch { toast.error("Could not start video job."); setBusy(false); }
  };

  return (
    <div>
      <div className="overline mb-2">video studio · talking-head → short-form</div>
      <h1 className="font-mono font-extrabold tracking-tighter text-3xl mb-6">New vertical cut</h1>

      <div className="inline-flex border border-border mb-8" data-testid="mode-toggle">
        <button data-testid="mode-edit" onClick={() => setGenMode(false)} className={`px-5 py-2.5 font-mono text-sm transition-colors ${!genMode ? "bg-primary text-black" : "text-muted-foreground hover:bg-secondary"}`}>Footage → Reel</button>
        <button data-testid="mode-generate" onClick={() => setGenMode(true)} className={`px-5 py-2.5 font-mono text-sm border-l border-border transition-colors ${genMode ? "bg-primary text-black" : "text-muted-foreground hover:bg-secondary"}`}>Script / Idea → Reel</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-border border border-border mb-10">
        <div className="lg:col-span-2 bg-[#18181B] p-6">
          <div className="overline mb-4">1 · {genMode ? "idea or script (required)" : "raw footage"}</div>
          {genMode ? (
            <>
            <textarea data-testid="generate-idea" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5}
              placeholder='Describe the reel. e.g. "A 30s ad for Relay AI — an app that turns raw footage or a script into a finished Instagram reel. Energetic, founder-to-camera tone, end with: try Relay AI today."'
              className="w-full bg-background border border-border p-4 font-mono text-sm focus:border-primary outline-none transition-colors" />
            <div className="overline mb-3 mt-6">scene visuals</div>
            <div className="inline-flex border border-border" data-testid="motion-toggle">
              <button data-testid="motion-stills" onClick={() => setMotion(false)} className={`px-4 py-2.5 font-mono text-sm transition-colors ${!motion ? "bg-primary text-black" : "text-muted-foreground hover:bg-secondary"}`}>Ken-Burns stills</button>
              <button data-testid="motion-clips" onClick={() => setMotion(true)} className={`px-4 py-2.5 font-mono text-sm border-l border-border transition-colors ${motion ? "bg-primary text-black" : "text-muted-foreground hover:bg-secondary"}`}>AI motion clips</button>
            </div>
            <div className="font-mono text-xs text-muted-foreground mt-2">{motion ? "Each scene becomes a generated moving clip (ltx image-to-video). Richer, slower, costs more." : "Each scene is an AI still with a Ken-Burns pan/zoom. Fast and cheap."}</div>
            </>
          ) : (
          <div data-testid="video-dropzone" onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
            className={`border border-dashed p-10 flex flex-col items-center justify-center cursor-pointer transition-colors ${drag ? "border-primary bg-[#1f1a15]" : "border-border hover:border-muted-foreground"}`}>
            <input ref={inputRef} data-testid="video-upload-input" type="file" accept=".mp4,.mov,.webm,.mkv,video/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
            {file ? (
              <div className="flex items-center gap-3 font-mono text-sm">
                <FileVideo size={28} className="text-primary" weight="fill" />
                <div><div>{file.name}</div><div className="text-muted-foreground text-xs">{(file.size / 1024 / 1024).toFixed(1)} MB</div></div>
              </div>
            ) : (
              <>
                <UploadSimple size={32} className="text-muted-foreground mb-3" />
                <div className="font-mono text-sm">drop talking-head video or click to browse</div>
                <div className="overline mt-2" style={{ fontSize: "0.55rem" }}>mp4 · mov · 5–20 min raw</div>
              </>
            )}
          </div>
          )}

          {!genMode && (<>
          <div className="overline mb-3 mt-6">3 · optional direction</div>
          <textarea data-testid="video-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
            placeholder='e.g. "Lead with the strongest insight, keep it punchy, friendly tone"'
            className="w-full bg-background border border-border p-4 font-mono text-sm focus:border-primary outline-none transition-colors" />
          </>)}
        </div>

        <div className="bg-[#18181B] p-6 flex flex-col">
          <div className="overline mb-4">2 · target</div>
          <div className="grid grid-cols-2 gap-px bg-border border border-border mb-5">
            {PLATFORMS.map((p) => (
              <button key={p.id} data-testid={`platform-${p.id}`} onClick={() => { setPlatform(p.id); setTargetLen(p.len); }}
                className={`p-3 font-mono text-sm transition-colors ${platform === p.id ? "bg-primary text-black" : "bg-[#18181B] hover:bg-secondary"}`}>{p.label}</button>
            ))}
          </div>
          <div className="flex items-center justify-between mb-2"><span className="font-mono text-sm">target length</span><span className="font-mono font-bold text-primary" data-testid="target-len-value">{targetLen}s</span></div>
          <input data-testid="target-len-slider" type="range" min={15} max={90} step={5} value={targetLen} onChange={(e) => setTargetLen(Number(e.target.value))} className="w-full accent-[#FF5500] mb-6" />
          <div className="mt-auto">
            <button data-testid="generate-video-button" disabled={(genMode ? !prompt.trim() : !file) || busy} onClick={generate}
              className="w-full bg-primary text-black font-mono font-bold py-3 flex items-center justify-center gap-2 hover:bg-[#FF7733] hover:-translate-y-[1px] transition-transform disabled:opacity-40">
              <Sparkle weight="fill" size={18} /> {busy ? "starting…" : "Generate cut"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="overline">recent video runs</div>
        <button onClick={() => nav("/video/history")} className="font-mono text-xs text-primary flex items-center gap-1 hover:gap-2 transition-all">all <ArrowRight size={12} /></button>
      </div>
      <div className="border border-border">
        {recent.length === 0 && <div className="p-6 font-mono text-sm text-muted-foreground bg-[#18181B]">No video runs yet.</div>}
        {recent.map((r) => (
          <button key={r.id} data-testid={`recent-video-${r.id}`} onClick={() => nav(`/video/runs/${r.id}`)}
            className="w-full flex items-center justify-between p-4 border-b border-border last:border-b-0 bg-[#18181B] hover:bg-secondary transition-colors text-left">
            <div className="flex items-center gap-3 min-w-0">
              <FilmSlate size={18} className="text-muted-foreground shrink-0" />
              <span className="font-mono text-sm truncate">{r.original_filename}</span>
              <span className="overline shrink-0" style={{ fontSize: "0.5rem" }}>{r.platform} · {r.target_len}s</span>
            </div>
            <span className="font-mono text-xs shrink-0" style={{ color: r.status === "completed" ? "#00E559" : r.status === "failed" ? "#FF3333" : "#A1A1AA" }}>{r.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
